import { isSimpleValidEmail } from '@/lib/validation/email';
import {
  optionalPhoneError,
  requiredPhoneError,
  toCallingCountryCode,
} from '@/lib/intlPhoneValidation';
import type { FormValidationMessages } from '@/lib/validation/formValidationMessages';

/** Поля участника шага анкет (registration-flow-page), нужные для проверки. */
export type RegistrationFlowParticipantValidationInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country?: string;
  isChampionshipParticipant: boolean;
  isSolo: boolean;
  partner2FirstName?: string;
  partner2LastName?: string;
  partner2Email?: string;
  partner2Phone?: string;
  partner2City?: string;
  partner2Country?: string;
  partner2ContactValue?: string;
  partner2Role?: string;
};

export type RegistrationFlowParticipantValidationOpts = {
  /**
   * Карточка второго партнёра как отдельного участника: у строки нет partner2*,
   * проверять пару не нужно.
   */
  skipChampionshipCopartnerRequirements?: boolean;
  /**
   * Телефон в основной строке (не partner2*): `true` = необязателен (secondary). Если опция не
   * передана — телефон **обязателен**.
   */
  mainParticipantPhoneOptional?: boolean;
};

export function registrationFlowParticipantFieldErrors(
  p: RegistrationFlowParticipantValidationInput,
  m: FormValidationMessages,
  opts?: RegistrationFlowParticipantValidationOpts,
): Record<string, string> {
  const newErrors: Record<string, string> = {};

  if (!p.firstName.trim()) newErrors.firstName = m.firstNameRequired;
  if (!p.lastName.trim()) newErrors.lastName = m.lastNameRequired;
  if (!p.email.trim()) newErrors.email = m.emailRequired;
  else if (!isSimpleValidEmail(p.email)) newErrors.email = m.emailInvalid;
  const countryCode = toCallingCountryCode(p.country);
  const phoneErr = opts?.mainParticipantPhoneOptional
    ? optionalPhoneError(p.phone, { invalid: m.phoneInvalid }, countryCode)
    : requiredPhoneError(
        p.phone,
        { required: m.phoneRequired, invalid: m.phoneInvalid },
        countryCode,
      );
  if (phoneErr) newErrors.phone = phoneErr;

  if (
    p.isChampionshipParticipant &&
    !opts?.skipChampionshipCopartnerRequirements &&
    !p.isSolo
  ) {
    if (!p.partner2FirstName?.trim())
      newErrors.partner2FirstName = m.partnerFirstNameRequired;
    if (!p.partner2LastName?.trim())
      newErrors.partner2LastName = m.partnerLastNameRequired;
    if (!p.partner2Email?.trim())
      newErrors.partner2Email = m.partnerEmailRequired;
    else if (p.partner2Email && !isSimpleValidEmail(p.partner2Email)) {
      newErrors.partner2Email = m.partnerEmailInvalid;
    }
    const partnerPhoneErr = optionalPhoneError(
      p.partner2Phone ?? '',
      { invalid: m.phoneInvalid },
      toCallingCountryCode(p.partner2Country),
    );
    if (partnerPhoneErr) newErrors.partner2Phone = partnerPhoneErr;
  }

  return newErrors;
}

export function isRegistrationFlowParticipantCompletelyValid(
  p: RegistrationFlowParticipantValidationInput,
  m: FormValidationMessages,
  opts?: RegistrationFlowParticipantValidationOpts,
): boolean {
  const countryCode = toCallingCountryCode(p.country);
  const phoneOk = opts?.mainParticipantPhoneOptional
    ? optionalPhoneError(p.phone, { invalid: m.phoneInvalid }, countryCode) ===
      undefined
    : requiredPhoneError(
        p.phone,
        { required: m.phoneRequired, invalid: m.phoneInvalid },
        countryCode,
      ) === undefined;

  const basicValid = !!(
    p.firstName.trim() &&
    p.lastName.trim() &&
    p.email.trim() &&
    isSimpleValidEmail(p.email) &&
    phoneOk
  );

  if (!basicValid) return false;

  if (
    p.isChampionshipParticipant &&
    !opts?.skipChampionshipCopartnerRequirements &&
    !p.isSolo
  ) {
    return !!(
      p.partner2FirstName?.trim() &&
      p.partner2LastName?.trim() &&
      p.partner2Email?.trim() &&
      isSimpleValidEmail(p.partner2Email || '') &&
      optionalPhoneError(
        p.partner2Phone ?? '',
        { invalid: m.phoneInvalid },
        toCallingCountryCode(p.partner2Country),
      ) === undefined
    );
  }

  return true;
}
